// Console delegation V1.0; 2026-10-07 09:05:47 CST; parent dcebd36069b2ddb3e45e586f2c90a0214b9a2a4f.
// 当前运营控制台源码维护说明，更新时间：2026-09-04 11:34 CST。游客可读取页面空态，最终写请求登录后原样继续。
import { ControlPlaneApiError, createBrowserTokenStorage, createHttpClient, type HttpClient } from '@hotx-cdn/shared';
import type { AxiosRequestConfig } from 'axios';
import { createDelegatedStorage, inDelegationContext, readDelegation, endDelegation } from '../auth/delegationSession';
import { createConsoleApi } from './consoleApi';
import type { AccountProfile } from './types';
import {
  configureConsoleAuthenticationReader,
  requireConsoleAuthentication
} from '../auth/guestAuthGate';
import { resolveDomainCreateEndpoint } from '../features/domain-plan/domainPlanSubmission';

export interface ConsoleSessionApi {
  account: {
    profile(): Promise<AccountProfile>;
  };
}

export interface ConsoleSessionStorage {
  getAccessToken(): string | null;
  getCustomerId(): number | null;
  clear(): void;
}

export interface ConsoleSessionGuard {
  ensureCurrentCustomer(): Promise<boolean>;
}

export const MAX_CONSOLE_SESSION_SECONDS = 30 * 24 * 60 * 60;
const CONSOLE_SESSION_EXPIRES_AT_KEY = 'hotx-cdn:console:session-expires-at';
export const CONSOLE_SESSION_CHANGED_EVENT = 'hotx:console-session-changed';

export const tokenStorage = createDelegatedStorage(createBrowserTokenStorage('console'));

export function resolveConsoleApiBaseUrl(
  configured = import.meta.env.VITE_API_BASE_URL,
  origin: string | null | undefined = browserOrigin()
) {
  const configuredUrl = configured?.trim();
  if (configuredUrl) {
    return configuredUrl;
  }
  return origin?.startsWith('http') ? origin : 'https://console.hotxcdn.net';
}

export function saveConsoleSessionExpiry(expiresIn: number) {
  const cappedExpiresIn = Math.min(expiresIn, MAX_CONSOLE_SESSION_SECONDS);
  localStorage.setItem(CONSOLE_SESSION_EXPIRES_AT_KEY, String(Date.now() + cappedExpiresIn * 1000));
  return cappedExpiresIn;
}

export function clearConsoleSessionStorage() {
  const delegated = inDelegationContext();
  tokenStorage.clear();
  if (!delegated) localStorage.removeItem(CONSOLE_SESSION_EXPIRES_AT_KEY);
}

export function hasConsoleSession() {
  return Boolean(tokenStorage.getAccessToken());
}

export function getConsoleSessionExpiresAt() {
  if (inDelegationContext()) return readDelegation()?.expiresAt ?? null;
  const value = Number(localStorage.getItem(CONSOLE_SESSION_EXPIRES_AT_KEY));
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function isConsoleSessionExpired() {
  const expiresAt = getConsoleSessionExpiresAt();
  return expiresAt !== null && Date.now() >= expiresAt;
}

const baseHttpClient = createHttpClient({
  baseURL: resolveConsoleApiBaseUrl(),
  getAccessToken: () => inDelegationContext() ? null : tokenStorage.getAccessToken(),
  onUnauthorized: () => {
    clearConsoleSessionStorage();
    notifyConsoleSessionChanged();
  }
});

baseHttpClient.raw.interceptors.request.use(config => {
  const delegation = readDelegation();
  if (delegation && !config.url?.startsWith('/api/v1/auth/delegation/exchange')) {
    config.headers.set('X-HotX-Delegation', delegation.sessionToken);
  }
  return config;
});

// Capture the request's identity so a delayed 401 cannot clear a newer session.
async function delegatedRequest<T>(request: () => Promise<T>): Promise<T> {
  const token = readDelegation()?.sessionToken;
  try { return await request(); }
  catch (error) {
    if (token && token === readDelegation()?.sessionToken && isExplicitAuthFailure(error)) {
      endDelegation(); notifyConsoleSessionChanged();
      window.location.replace('/delegation-ended');
    }
    throw error;
  }
}

configureConsoleAuthenticationReader(() => hasConsoleSession()
  || import.meta.env.VITE_UAT_IP_BYPASS_ENABLED === 'true');

// 更新时间：2026-09-04 11:34 CST。游客写请求在此统一暂停，登录完成后继续同一个 Promise，业务页面无需重建表单。
export const httpClient: HttpClient = {
  raw: baseHttpClient.raw,
  get: <T>(url: string, config?: AxiosRequestConfig) => delegatedRequest(() => baseHttpClient.get<T>(url, config)),
  post: async <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) => {
    await waitForAuthenticationBeforeMutation(url);
    // Domain plan entry V1.0 / 2026-09-09 CST / parent 15a7174ab:
    // package-aware domain creation is coordinated server-side; legacy and pure-IP
    // mutations keep their original endpoint and mature behavior.
    const targetUrl = url === '/api/v1/domains' ? resolveDomainCreateEndpoint(body) : url;
    return delegatedRequest(() => baseHttpClient.post<T, B>(targetUrl, body, config));
  },
  put: async <T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) => {
    await waitForAuthenticationBeforeMutation(url);
    return delegatedRequest(() => baseHttpClient.put<T, B>(url, body, config));
  },
  delete: async <T>(url: string, config?: AxiosRequestConfig) => {
    await waitForAuthenticationBeforeMutation(url);
    return delegatedRequest(() => baseHttpClient.delete<T>(url, config));
  }
};

export const consoleApi = createConsoleApi(httpClient);

interface ConsoleAuthTokenPayload {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  mustChangePassword: boolean;
  customerId?: number | null;
}

export interface GuestPasswordLoginRequest {
  email: string;
  password: string;
}

export interface GuestRegistrationRequest {
  customerName: string;
  email: string;
  verificationCode: string;
  password: string;
}

export async function loginConsoleFromGuestDialog(request: GuestPasswordLoginRequest) {
  const token = await baseHttpClient.post<ConsoleAuthTokenPayload, GuestPasswordLoginRequest>('/api/v1/auth/login', request);
  activateConsoleSession(token);
}

export async function sendConsoleRegistrationCode(email: string) {
  await baseHttpClient.post<void, { email: string }>('/api/v1/auth/register/email-code', { email });
}

export async function registerConsoleFromGuestDialog(request: GuestRegistrationRequest) {
  const token = await baseHttpClient.post<ConsoleAuthTokenPayload, GuestRegistrationRequest>('/api/v1/auth/register', request);
  activateConsoleSession(token);
}

export function requestConsoleGoogleAuthorization(state: string) {
  return baseHttpClient.post<{ authorizationUrl: string }, { state: string }>('/api/v1/auth/google/authorization-url', { state });
}

export function createConsoleSessionGuard(
  api: ConsoleSessionApi,
  storage: ConsoleSessionStorage
): ConsoleSessionGuard {
  return {
    async ensureCurrentCustomer() {
      if (!storage.getAccessToken() || !getConsoleSessionExpiresAt() || isConsoleSessionExpired()) {
        storage.clear();
        if (!inDelegationContext()) localStorage.removeItem(CONSOLE_SESSION_EXPIRES_AT_KEY);
        notifyConsoleSessionChanged();
        return false;
      }
      try {
        const profile = await api.account.profile();
        if (storage.getCustomerId() !== profile.customerId) {
          storage.clear();
          notifyConsoleSessionChanged();
          return false;
        }
        return true;
      } catch (e) {
        if (isExplicitAuthFailure(e)) {
          storage.clear();
          return false;
        }
        return true;
      }
    }
  };
}

export const consoleSessionGuard = createConsoleSessionGuard(consoleApi, tokenStorage);

function isExplicitAuthFailure(error: unknown) {
  return error instanceof ControlPlaneApiError
    && (error.status === 401 || error.code === 'UNAUTHORIZED' || error.code === 'TOKEN_EXPIRED');
}

async function waitForAuthenticationBeforeMutation(url: string) {
  if (isAuthenticationEndpoint(url)) {
    return;
  }
  await requireConsoleAuthentication(mutationReason(url));
}

function isAuthenticationEndpoint(url: string) {
  return url.startsWith('/api/v1/auth/') || url.startsWith('/ops/api/v1/auth/');
}

function mutationReason(url: string) {
  if (url.includes('/domains')) return '登录后保存域名配置';
  if (url.includes('/certificates') || url.includes('/https')) return '登录后提交证书操作';
  if (url.includes('/cache')) return '登录后提交缓存刷新';
  if (url.includes('/packages') || url.includes('/orders')) return '登录后确认套餐或订单';
  if (url.includes('/recharge')) return '登录后继续充值';
  return '登录后继续保存当前操作';
}

function activateConsoleSession(token: ConsoleAuthTokenPayload) {
  // 更新时间：2026-09-04 11:34 CST。兼容未携带客户编号的认证响应，明确存为 null，禁止写入无效字符串。
  tokenStorage.save({ ...token, customerId: token.customerId ?? null });
  saveConsoleSessionExpiry(token.expiresIn);
  notifyConsoleSessionChanged();
}

function notifyConsoleSessionChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONSOLE_SESSION_CHANGED_EVENT));
  }
}

function browserOrigin() {
  return typeof window === 'undefined' ? undefined : window.location.origin;
}
